import { PANEL_CSS } from './styles';
import { log } from './logger';

const ROOT_ATTR = 'data-agent-sdk-root';

const Z_INDEX = 2147483000;

let host: HTMLElement | null = null;
let shadow: ShadowRoot | null = null;

function installStyles(root: ShadowRoot): void {
  if (typeof CSSStyleSheet === 'function' && 'adoptedStyleSheets' in root) {
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(PANEL_CSS);
      root.adoptedStyleSheets = [sheet];
      return;
    } catch (error) {
      log.warn('constructed stylesheet unavailable, using <style>', error);
    }
  }
  const style = document.createElement('style');
  style.textContent = PANEL_CSS;
  root.appendChild(style);
}

const CONTAINED_EVENTS = [
  'pointerdown',
  'pointerup',
  'mousedown',
  'mouseup',
  'click',
  'touchstart',
  'touchend',
] as const;

/**
 * Shadow DOM events are composed: a mousedown inside the panel still reaches the
 * host page's document, retargeted to this element. Host apps commonly close
 * menus and dropdowns from a document-level mousedown, so interacting with the
 * panel was dismissing whatever the user had open — including the very menu an
 * approved click was about to act on, which then failed as a missing element.
 *
 * Stopping at the host means listeners inside the shadow root have already run,
 * so only the host page is kept from seeing input that was never meant for it.
 */
function containInput(element: HTMLElement): void {
  for (const type of CONTAINED_EVENTS) {
    element.addEventListener(type, (event) => event.stopPropagation());
  }
}

export function mountShell(theme: 'light' | 'dark' | 'system'): ShadowRoot {
  if (shadow) return shadow;

  host = document.createElement('div');
  host.setAttribute(ROOT_ATTR, '');
  host.style.setProperty('z-index', String(Z_INDEX));

  const resolved =
    theme === 'system'
      ? window.matchMedia?.('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light'
      : theme;
  host.setAttribute('data-theme', resolved);

  shadow = host.attachShadow({ mode: 'open' });
  installStyles(shadow);
  containInput(host);
  document.body.appendChild(host);

  if (theme === 'system') {
    window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener('change', (event) => {
      host?.setAttribute('data-theme', event.matches ? 'dark' : 'light');
    });
  }

  return shadow;
}

export function shellRoot(): ShadowRoot | null {
  return shadow;
}

export function shellHost(): HTMLElement | null {
  return host;
}

export function destroyShell(): void {
  host?.remove();
  host = null;
  shadow = null;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}
